import React, { memo, useState } from "react";
import Modal from "react-modal";
import { PlainTextInput, Input } from "components/Inputs";
import { Lookup as RoleLookup } from "components/Roles";
import { Button, Row, Col, Title, ListGroup, ListGroupItem } from "components/Shared";
import { Modal as ConditionModal } from "components/Conditions";
import { Modal as DependencyModal } from "components/Dependencies";
import { translate } from "lib";
// @ts-ignore
import validate from "validate.js";
import _ from "lodash";
import DraggableConditions from "./DraggableConditions";

const constraints = {
	name: {
		presence: true
	},
	role: {
		presence: true
	},
	length: {
		numericality: {
			greaterThan: 0,
			onlyInteger: true
		},
		presence: true
	},
	sla_target_days: {
		numericality: {
			onlyInteger: true,
			greaterThanOrEqualTo: 0,
			allowBlank: true
		}
	},
	sla_breached_days: {
		numericality: {
			onlyInteger: true,
			greaterThanOrEqualTo: 0,
			allowBlank: true
		}
	}
};

const TaskModal = ({
	task: defaultTask,
	isOpen,
	phase,
	template,
	onUpdate: defaultOnUpdate,
	deleteTask,
	doCloseModal
}: {
	task?: tcpinpoint.Task;
	isOpen: boolean;
	phase?: tcpinpoint.Phase;
	template?: tcpinpoint.Template;
	deleteTask?: (task: any) => any;
	onUpdate?: (task: any) => any;
	doCloseModal?: () => any;
}) => {
	const [task, setTask] = useState<any>(() => {
		const base: any = {
			errors: null,
			start_with: false,
			name: "",
			description: "",
			length: "0",
			role: false,
			spectators: [],
			conditions: [],
			dependencies: [],
			confirmDelete: false,
			dependencyModal: false,
			conditionModal: false,
			// The condition the modal is editing. null means Add — ConditionModal
			// reads its `item` prop and mints a fresh uuid when there is none.
			conditionItem: null,
			...defaultTask
		};
		// SLA thresholds default to 0 (never nil) even when editing a task whose
		// stored values are null (project-start task, legacy tasks).
		return {
			...base,
			sla_target_days: base.sla_target_days ?? 0,
			sla_breached_days: base.sla_breached_days ?? 0
		};
	});

	const onUpdate = () => {
		const baseErrors = validate(task, constraints) ?? {};

		const hasErrors = Object.keys(baseErrors).length > 0;
		if (hasErrors) {
			setTask((task: any) => ({ ...task, errors: baseErrors }));
		} else {
			defaultOnUpdate && defaultOnUpdate(task);
			doCloseModal && doCloseModal();
		}
	};

	const { id, spectators, conditions, dependencies, dependencyModal, conditionModal, conditionItem, start_with, end_with } = task;
	const tasks = _.flatten(_.map(_.get(template, "body.phases", []), phase => phase.tasks));
	const isStartSet = _.find(tasks, { start_with: true });
	// Prefer the flat `property_id` attribute (api-contract §1.3); fall back to the
	// JSON:API `property` relationship for templates fetched before this attribute existed.
	const templatePropertyId = template?.property_id ?? template?.property?.id;

	return (
		<Modal
			ariaHideApp={false}
			isOpen={isOpen}
			onRequestClose={doCloseModal}
			className="task-edit-modal"
			contentLabel="Task Edit Modal"
		>
			<header>
				<div className="modal-header-body">
					<Title>{translate("edit_template_task_modal_header")}</Title>
				</div>
				<button type="button" className="close" aria-label="Close" onClick={doCloseModal}>
					<span aria-hidden="true">×</span>
				</button>
			</header>

			<section>
				<Row>
					<Col>
						<Input
							defaultState={task}
							required={constraints}
							name="name"
							onChange={(name: string) => setTask((task: any) => ({ ...task, name }))}
						/>
						<PlainTextInput
							defaultState={task}
							required={constraints}
							limit={300}
							name="description"
							hint="A reminder of what this task is about"
							onChange={(description: string) => setTask((task: any) => ({ ...task, description }))}
						/>
						<Input
							required={constraints}
							defaultState={task}
							name="length"
							min={1}
							step={1}
							suffix="calendar days"
							onChange={(length: string) => setTask((task: any) => ({ ...task, length }))}
							type="number"
							hint="Number of Calendar Days estimated to complete the task"
						/>
					</Col>
				</Row>
				<Row>
					<Col>
						<fieldset id="sla">
							<legend style={{ fontSize: "13px", fontWeight: 600 }}>{translate("sla_configuration")}</legend>
							<Input
								required={constraints}
								defaultState={task}
								name="sla_target_days"
								type="number"
								min={0}
								step={1}
								suffix="days"
								onChange={(sla_target_days: string) =>
									setTask((task: any) => ({ ...task, sla_target_days }))
								}
							/>
							<Input
								required={constraints}
								defaultState={task}
								name="sla_breached_days"
								type="number"
								min={0}
								step={1}
								suffix="days"
								onChange={(sla_breached_days: string) =>
									setTask((task: any) => ({ ...task, sla_breached_days }))
								}
							/>
						</fieldset>
					</Col>
				</Row>
				<hr />
				<Row>
					<Col>
						<fieldset>
							<RoleLookup
								defaultState={task}
								required={constraints}
								hint="The Role responsible for completing task."
								name="role"
								label="primary_responsibility"
								onChange={(role: any) => setTask((task: any) => ({ ...task, role }))}
							/>
							<RoleLookup
								label="Spectator Roles"
								required={constraints}
								hint="The spectator roles on the task"
								name="spectators"
								defaultState={task}
								onChange={(role: any) =>
									setTask((task: any) => {
										if (!_.find(task?.spectators, { name: role?.name })) {
											const spectators = [...task?.spectators];
											spectators.push(role);
											return { ...task, spectators };
										}
										return task;
									})
								}
								defaultOption={"null"}
							>
								<ListGroup
									resource="spectators"
									collection={spectators}
									renderRow={row => (
										<ListGroupItem
											key={row.id}
											subheading={row.name}
											onRemove={() => {
												setTask((task: any) => ({
													...task,
													spectators: _.filter(task?.spectators, spectator => spectator.name !== row.name)
												}));
											}}
										/>
									)}
								/>
							</RoleLookup>
						</fieldset>
					</Col>
					<Col>
						<fieldset id="dependencies">
							<label>{translate("dependencies")}</label>
							{(!isStartSet || _.get(isStartSet, "id") === id) && (
								<Input
									type="checkbox"
									defaultState={task}
									name="start_with"
									required={constraints}
									onChange={(start_with: any) => setTask((task: any) => ({ ...task, start_with }))}
								/>
							)}
							{!start_with && (
								<React.Fragment>
									<ListGroup
										resource="dependencies"
										collection={dependencies}
										onAdd={() => setTask((task: any) => ({ ...task, dependencyModal: true }))}
										renderRow={row => (
											<ListGroupItem
												key={row.id}
												heading={row.name}
												onRemove={() =>
													setTask((task: any) => ({
														...task,
														dependencies: _.filter(task?.dependencies, dependency => dependency.id !== row.id)
													}))
												}
											/>
										)}
									/>
									<DependencyModal
										isOpen={dependencyModal}
										doCloseModal={() => setTask((task: any) => ({ ...task, dependencyModal: false }))}
										onUpdate={(dependency: any) =>
											setTask((task: any) => {
												const deps = [...task?.dependencies];
												deps.push(dependency);
												return { ...task, dependencies: _.uniqBy(deps, "id") };
											})
										}
										task={task}
										template={template}
									/>
								</React.Fragment>
							)}
						</fieldset>
						<fieldset id="conditions">
							<label>{translate("conditions")}</label>
							<DraggableConditions
								conditions={conditions}
								onAdd={() => setTask((task: any) => ({ ...task, conditionModal: true, conditionItem: null }))}
								onEdit={(condition: any) =>
									setTask((task: any) => ({ ...task, conditionModal: true, conditionItem: condition }))
								}
								onUpdate={(reorderedConditions: any[]) =>
								setTask((task: any) => ({ ...task, conditions: reorderedConditions }))
								}
							/>
							{/* One modal for both Add and Edit. ConditionModal already
							    supports editing: it prefills from `item`, keeps that
							    item's id, and its one-submission-per-task check skips
							    the row being edited (`c.id !== data.id`). */}
							<ConditionModal
								isOpen={conditionModal}
								item={conditionItem}
								propertyId={templatePropertyId}
								existingConditions={conditions}
								doCloseModal={() => setTask((task: any) => ({ ...task, conditionModal: false, conditionItem: null }))}
								onUpdate={(condition: any) =>
								setTask((task: any) => {
									const cons = [...(task?.conditions || [])];
									// Replace when the id is already in the list — an edit
									// returns the SAME id, so pushing would duplicate the row.
									// A new condition carries a fresh uuid and falls through
									// to the append.
									const index = cons.findIndex((existing: any) => existing.id === condition.id);
									if (index >= 0) {
										cons[index] = { ...cons[index], ...condition };
									} else {
										cons.push(condition);
									}
									return { ...task, conditions: cons };
								})
								}
							/>
						</fieldset>
					</Col>
				</Row>
			</section>
			<footer>
				<Button onClick={() => onUpdate()} />
				{!end_with && !start_with && id && (
					<Button kind="destroy" confirm onClick={() => deleteTask && deleteTask(task)} />
				)}
			</footer>
		</Modal>
	);
};

export default memo(TaskModal);
